import {Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren} from '@angular/core';
import { AccordionModule, AccordionPanelComponent } from 'ngx-bootstrap/accordion';
import { WindowSizeService } from '@ally/remarketing/shared/services';
import { Store } from '@ngxs/store';
import {
  EditPostingState,
  UpdateSelectedPostingForm,
  EditPostingFormNames,
  EditPostingForms,
  PostingFormTitle,
  PostingTypes,
  SubmitPostingEvent
} from 'remarketing-angular-library';
import { EditPostingUtilsService } from '../../services/edit-posting-utils.service';
import { PostingFormTrackingService } from '../../services/posting-form-tracking.service';
import {takeUntil} from 'rxjs/operators';
import { SaComponent } from 'smartauction-frontend-micro-library';
import { TranslocoDirective } from '@jsverse/transloco';
import { ErrorIconComponent } from 'remarketing-ui-components-lib';
import { DescriptionFormComponent } from '../description-form/description-form.component';
import {
  EditPostingButtonsComponent
} from '../edit-posting-buttons/edit-posting-buttons.component';
import { LocationFormComponent } from '../location-form/location-form.component';
import { ExteriorFormComponent } from '../exterior-form/exterior-form.component';
import { InteriorFormComponent } from '../interior-form/interior-form.component';
import { MechanicalFormComponent } from '../mechanical-form/mechanical-form.component';
import {
  FullDescriptionFormComponent
} from '../full-description-form/full-description-form.component';
import { PhotosFormComponent } from '../photos-form/photos-form.component';
import { ConditionFormComponent } from '../condition-form/condition-form.component';
import { DamagesFormComponent } from '../damages-form/damages-form.component';
import { TiresFormComponent } from '../tires-form/tires-form.component';
import {
  AdditionalDisclosuresFormComponent
} from '../additional-disclosures-form/additional-disclosures-form.component';
import {
  PricingAndFeesFormComponent
} from '../pricing-and-fees-form/pricing-and-fees-form.component';
import { CommonModule } from '@angular/common';
@Component({
    selector: 'app-edit-posting-mobile',
    templateUrl: './edit-posting-mobile.component.html',
    styleUrls: ['./edit-posting-mobile.component.scss'],
    imports: [
        TranslocoDirective,
        AccordionModule,
        ErrorIconComponent,
        DescriptionFormComponent,
        EditPostingButtonsComponent,
        LocationFormComponent,
        ExteriorFormComponent,
        InteriorFormComponent,
        MechanicalFormComponent,
        FullDescriptionFormComponent,
        PhotosFormComponent,
        ConditionFormComponent,
        DamagesFormComponent,
        TiresFormComponent,
        AdditionalDisclosuresFormComponent,
        PricingAndFeesFormComponent,
        CommonModule
    ]
})
export class EditPostingMobileComponent extends SaComponent implements OnInit {
  @ViewChildren(AccordionPanelComponent) accordionPanels: QueryList<AccordionPanelComponent>;
  accordionState = {
    'information-description': false,
    'information-location': false,
    'features-exterior': false,
    'features-interior': false,
    'features-mechanical': false,
    'features-full-description': false,
    'vehicle-photos': false,
    'vehicle-condition': false,
    'vehicle-damages': false,
    'vehicle-tires': false,
    'disclosure-additional': false,
    'vehicle-pricing-and-fees': false
  };
  isDisabled = false;
  clickedPanelClass: string;
  closingAccordion = false;
  isDesktop = false;
  @Output() continueSubmission = new EventEmitter<SubmitPostingEvent>();
  @Input() disableLinksForHold: boolean;
  EditPostingFormNames = EditPostingFormNames;
  validationFailedForms: string[] = [];
  isYellowLightChecked: boolean;
  postingType: PostingTypes;
  postingTypeString: PostingFormTitle;

  constructor(
    private readonly windowSizeService: WindowSizeService,
    private readonly store: Store,
    private readonly editPostingUtilsService: EditPostingUtilsService,
    private readonly postingFormTrackingService: PostingFormTrackingService
  ) {
    super();
  }

  ngOnInit() {
    this.editPostingUtilsService.clearFailedValidationFormsArray();
    this.editPostingUtilsService.validationFailedForms.subscribe(forms => {
      this.validationFailedForms = forms;
    });
    this.store
      .select(EditPostingState.postingType)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(postingType => (this.postingType = postingType));
    this.setPostingFormValues();
  }

  setPostingFormValues() {
    switch (this.postingType) {
      case PostingTypes.EDIT:
        this.postingTypeString = PostingFormTitle.EDIT;
        break;
      case PostingTypes.REPOST_AFTER_60_DAYS:
      case PostingTypes.REPOST_BEFORE_60_DAYS:
        this.postingTypeString = PostingFormTitle.REPOST;
        break;
      case PostingTypes.NEW:
        this.postingTypeString = this.isNewThirdPartyPosting()
          ? PostingFormTitle.NEW_THIRD_PARTY
          : PostingFormTitle.NEW;
        break;
    }
  }

  isNewThirdPartyPosting(): boolean {
    const isThirdParty = this.store.selectSnapshot(EditPostingState.updatedPosting)?.inventoryItem?.postingInformation
      ?.thirdPartyPosting;
    return !!isThirdParty && this.postingType === PostingTypes.NEW;
  }

  get editPostingForms(): typeof EditPostingForms {
    return EditPostingForms;
  }

  clickAccordion(panelClass: string, selectedPostingForm: EditPostingForms) {
    this.store.dispatch(new UpdateSelectedPostingForm(selectedPostingForm));
    this.clickedPanelClass = panelClass;
    const openAccordion = this.accordionPanels.find(accordionPanel => accordionPanel.isOpen);
    const accordion = this.accordionPanels.find(accordionPanel => accordionPanel.panelClass === panelClass);
    if (!openAccordion || (openAccordion && openAccordion.panelClass === panelClass)) {
      accordion.isOpen = !accordion.isOpen;
      this.closingAccordion = !accordion.isOpen;
      this.accordionState[panelClass] = accordion.isOpen;
      return;
    }
    if (openAccordion && openAccordion.panelClass !== panelClass) {
      openAccordion.isOpen = false;
      this.closingAccordion = false;
      accordion.isOpen = true;
      this.accordionState[panelClass] = true;
    }
  }

  showErrorIcon(formName: string): boolean {
    if (this.postingType === PostingTypes.NEW) {
      return this.validationFailedForms.includes(formName) || this.newPostingViewedButInvalid(formName);
    } else {
      return this.validationFailedForms.includes(formName);
    }
  }

  showSuccessIcon(formName: string): boolean {
    return !this.validationFailedForms.includes(formName) && this.newPostingViewedAndValid(formName);
  }

  yellowLightSelected($event) {
    this.isYellowLightChecked = $event;
  }

  get isNewPosting() {
    return this.postingType === PostingTypes.NEW;
  }

  newPostingViewedAndValid(formName: string) {
    return this.postingFormTrackingService.isNewPostingViewedAndValid(this.postingType, formName);
  }

  newPostingViewedButInvalid(formName: string) {
    return this.postingFormTrackingService.isNewPostingViewedButInvalid(this.postingType, formName);
  }

  /* Disable tire link if vehicle is pending inspection.
   */
  get disableTires(): boolean {
    return this.disableLinksForHold;
  }
}
